import { useCallback, useEffect, useRef, useState } from 'react';
import API from '../api';

// ── 자체(시스템) 메일: 메일주소 공용 컴포넌트 ──────────────────────────────
// 환경설정 > 회사정보에서 "시스템메일"을 선택하면 직원별 메일주소(아이디@도메인)를 등록한다.
// 등록된 주소는 서버(backend/utils/sysMail.js)가 한 곳에서 관리하고, 전자메일 작성창 자동완성/주소록 등
// IMS의 모든 화면이 같은 주소를 공유한다. 이 파일은 주소 입력 UI(가이드 포함)와, 다른 화면에 끼워 넣는
// 작은 안내 컴포넌트(SysMailGuideNote)를 제공한다.

export const MAIL_GUIDE = '회사명_이름_직책';
export const MAIL_EXAMPLE = 'ninesafety_hong_manager';

export function useSysMailStatus(reloadKey = 0) {
  const [st, setSt] = useState(null);
  useEffect(() => {
    let alive = true;
    API.get('/sysmail/status').then(r => { if (alive) setSt(r.data); }).catch(() => { if (alive) setSt({ enabled: false }); });
    return () => { alive = false; };
  }, [reloadKey]);
  return st;
}

// 입력칸 바로 아래에 보여주는 형식 가이드 (요청: "메일주소를 저장할때 입력칸 아래쪽에 가이드")
export function MailAddressGuide({ style }) {
  return (
    <div style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.6, ...style }}>
      <div>입력 형식 : <b style={{ color: '#185FA5' }}>{MAIL_GUIDE}</b> <span style={{ color: '#9ca3af' }}>(예: {MAIL_EXAMPLE})</span></div>
      <div style={{ color: '#9ca3af' }}>영문 소문자·숫자·점(.)·밑줄(_)·하이픈(-)만 사용, 3~64자. 한글은 영문으로 적어주세요.</div>
    </div>
  );
}

const inputStyle = { width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', outline: 'none' };
const btn = (bg, disabled) => ({ padding: '9px 18px', background: disabled ? '#cbd5e1' : bg, color: '#fff', border: 'none', borderRadius: 8, cursor: disabled ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 600 });

// 메일주소(아이디) 입력 + 중복확인 + 저장. admin 이면 다른 직원의 주소를 대신 지정한다.
export function SysMailAddressForm({ domain, initial = '', username, admin, onSaved, onCancel, canChange = true, saveLabel = '메일주소 저장' }) {
  const [local, setLocal] = useState(initial || '');
  const [check, setCheck] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const timer = useRef(null);

  useEffect(() => {
    clearTimeout(timer.current);
    const v = local.trim();
    if (!v || v.toLowerCase() === (initial || '').toLowerCase()) { setCheck(null); return undefined; }
    timer.current = setTimeout(() => {
      API.get('/sysmail/mailbox/check', { params: { local: v } }).then(r => setCheck(r.data)).catch(() => setCheck(null));
    }, 350);
    return () => clearTimeout(timer.current);
  }, [local, initial]);

  const save = async () => {
    setSaving(true); setErr('');
    try {
      const r = admin && username
        ? await API.put(`/sysmail/admin/mailboxes/${encodeURIComponent(username)}`, { local: local.trim() })
        : await API.put('/sysmail/mailbox', { local: local.trim() });
      onSaved && onSaved(r.data);
    } catch (e) { setErr(e?.response?.data?.message || '저장하지 못했습니다.'); }
    setSaving(false);
  };

  const disabled = !canChange || saving || !local.trim() || (check && check.ok === false);
  return (
    <div>
      <label style={{ fontSize: 13, color: '#555', fontWeight: 500, display: 'block', marginBottom: 6 }}>메일주소</label>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <input style={{ ...inputStyle, flex: 1 }} value={local} disabled={!canChange} autoFocus
          onChange={e => setLocal(e.target.value.replace(/\s/g, '').toLowerCase())}
          onKeyDown={e => { if (e.key === 'Enter' && !disabled) save(); }} />
        <span style={{ fontSize: 14, color: '#374151', whiteSpace: 'nowrap' }}>@{domain}</span>
      </div>
      {/* 입력칸 바로 아래 가이드 */}
      <MailAddressGuide style={{ marginTop: 6 }} />
      {check && <div style={{ marginTop: 6, fontSize: 12.5, color: check.ok ? '#15803d' : '#dc2626' }}>{check.ok ? '✔ ' : '✖ '}{check.message}</div>}
      {!canChange && <div style={{ marginTop: 6, fontSize: 12.5, color: '#b45309' }}>이미 등록된 메일주소는 관리자만 변경할 수 있습니다.</div>}
      {err && <div style={{ marginTop: 6, fontSize: 12.5, color: '#dc2626' }}>{err}</div>}
      <div style={{ display: 'flex', gap: 8, marginTop: 14, justifyContent: 'flex-end' }}>
        {onCancel && <button onClick={onCancel} style={{ ...btn('#6b7280'), background: '#f3f4f6', color: '#374151' }}>닫기</button>}
        <button onClick={save} disabled={disabled} style={btn('#185FA5', disabled)}>{saving ? '저장 중...' : saveLabel}</button>
      </div>
    </div>
  );
}

export function SysMailAddressModal({ domain, initial, canChange = true, username, admin, title = '메일주소 등록', onClose, onSaved }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: 480, maxWidth: '92vw', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 17 }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#888' }}>✕</button>
        </div>
        <SysMailAddressForm domain={domain} initial={initial} canChange={canChange} username={username} admin={admin} onSaved={onSaved} onCancel={onClose} />
      </div>
    </div>
  );
}

// 다른 화면(정보수정/사원등록 등)의 이메일 입력칸 아래에 끼워 넣는 안내.
// 시스템메일이 꺼져 있으면 아무것도 그리지 않으므로 기존 화면은 그대로다.
export function SysMailGuideNote({ style, guideOnly }) {
  const [reload, setReload] = useState(0);
  const st = useSysMailStatus(reload);
  const [open, setOpen] = useState(false);
  const done = useCallback(() => { setOpen(false); setReload(n => n + 1); }, []);
  if (!st || !st.enabled) return null;
  // guideOnly : 다른 사람 정보를 다루는 화면(사원등록 등)에서는 내 주소 대신 형식 안내만 보여준다
  if (guideOnly) {
    return (
      <div style={{ marginTop: 6, fontSize: 12, color: '#6b7280', lineHeight: 1.6, ...style }}>
        <div>사내(시스템) 메일주소는 <b>@{st.domain}</b> 형식으로 직원이 직접 등록하거나, 환경설정 &gt; 회사정보 &gt; 직원 메일주소 관리에서 지정합니다.</div>
        <MailAddressGuide />
      </div>
    );
  }
  return (
    <div style={{ marginTop: 6, fontSize: 12, color: '#6b7280', lineHeight: 1.6, ...style }}>
      <div>
        사내(시스템) 메일주소 : {st.address
          ? <b style={{ color: '#185FA5' }}>{st.address}</b>
          : <span style={{ color: '#b45309' }}>미등록</span>}
        {!st.address && <button type="button" onClick={() => setOpen(true)} style={{ marginLeft: 8, padding: '2px 8px', fontSize: 11.5, border: '1px solid #185FA5', color: '#185FA5', background: '#fff', borderRadius: 4, cursor: 'pointer' }}>등록</button>}
      </div>
      <MailAddressGuide />
      {open && <SysMailAddressModal domain={st.domain} onClose={() => setOpen(false)} onSaved={done} />}
    </div>
  );
}
